import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Mail, Loader2, RefreshCw } from 'lucide-react';
import PasswordInput from '../components/PasswordInput';
import { useTranslation } from '../context/LanguageContext';
import { useNotification } from '../context/NotificationContext';
import { usePermission } from '../context/PermissionContext';

const Login = () => {
  const navigate = useNavigate();
  const { notify } = useNotification();
  const { refreshPermissions } = usePermission();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [challengeId, setChallengeId] = useState('');
  const [captchaImageUrl, setCaptchaImageUrl] = useState('');
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { t } = useTranslation();

  // Fetch CAPTCHA on mount
  const fetchCaptcha = async () => {
    try {
      const res = await fetch('/api/auth/captcha', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (data.status === 'success') {
        setChallengeId(data.challenge_id);
        // Append cache-buster so refresh always loads a new image
        setCaptchaImageUrl(data.image_url + '?_=' + Date.now());
        setCaptchaAnswer('');
      }
    } catch (err) {
      console.error('Failed to fetch captcha:', err);
    }
  };

  useEffect(() => {
    fetchCaptcha();
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    setLoading(true);
    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, challenge_id: challengeId, captcha_answer: captchaAnswer }),
      });
      const data = await res.json();
      console.debug('Login response:', data);
      if (data.status === 'success') {
        console.debug('Storing token in localStorage:', data.token ? 'Token present' : 'Token missing!');
        localStorage.setItem('token', data.token);
        // 刷新权限，避免进入首页后菜单空白（PermissionContext 初始加载时还没有 token）
        refreshPermissions();
        console.debug('Token stored, navigating to /');
        navigate('/');
      } else {
        notify(data.message || t('auth.login_failed'), 'error');
        fetchCaptcha(); // Refresh captcha on failure
      }
    } catch (err) {
      console.error('Login error:', err);
      notify(t('common.error'), 'success');
    } finally {
      setLoading(false);
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-900 dark:to-slate-800 px-4">
      <div className="max-w-md w-full bg-white/90 dark:bg-slate-900/90 backdrop-blur-sm rounded-2xl shadow-2xl p-10 border border-slate-300/20 dark:border-slate-700/50">
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-blue-600 to-blue-800 text-white rounded-full shadow-lg">
            <span className="text-4xl font-bold tracking-tight">E</span>
          </div>
        </div>

        <form onSubmit={handleLogin} className="space-y-5">
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-slate-700 dark:text-slate-300">{t('auth.email')}</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 dark:text-slate-400" size={18} />
              <input 
                type="email"
                className="w-full pl-10 pr-4 py-2.5 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all dark:bg-slate-800 dark:text-white"
                placeholder={t('auth.email_placeholder')}
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
              />
            </div>
          </div>

          <PasswordInput 
            label={t('auth.password')} 
            value={password} 
            onChange={e => setPassword(e.target.value)} 
            required
          />

          {/* CAPTCHA */}
          {captchaImageUrl && (
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-slate-700 dark:text-slate-300">{t('auth.captcha')}</label>
              <div className="flex items-center gap-2">
                <img 
                  src={captchaImageUrl}
                  alt="captcha"
                  className="h-[60px] w-[200px] rounded-lg border border-slate-300 dark:border-slate-600 select-none"
                />
                <button
                  type="button"
                  onClick={fetchCaptcha}
                  className="p-2.5 border border-slate-300 dark:border-slate-600 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                  title={t('auth.captcha_refresh')}
                >
                  <RefreshCw size={18} className="text-slate-500 dark:text-slate-400" />
                </button>
              </div>
              <input 
                type="text"
                className="w-full pl-4 pr-4 py-2.5 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all dark:bg-slate-800 dark:text-white"
                placeholder={t('auth.captcha_placeholder')}
                value={captchaAnswer}
                onChange={e => setCaptchaAnswer(e.target.value.toUpperCase())}
                required
                autoComplete="off"
              />
            </div>
          )}

          <button 
            type="submit" 
            disabled={isSubmitting || loading}
            className={`w-full py-3.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-blue-800 transition-all flex items-center justify-center gap-2 shadow-lg hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0 ${isSubmitting ? 'opacity-60 cursor-not-allowed' : ''}`}
          >
            {isSubmitting ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                {t('common.processing')}
              </span>
            ) : loading ? (
              <Loader2 className="animate-spin" size={20} />
            ) : (
              t('auth.login_now')
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Login;
